import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import type { WatchlistItem, SortColumn, SortDirection, WatchlistStats } from '../types';
import { getAllChecklistIds, processItems, calculateStats } from '../utils/helpers';

// Fetch watchlist data
export function useWatchlist() {
  return useQuery({
    queryKey: ['watchlist'],
    queryFn: () => api.getWatchlist(),
  });
}

// Get all unique checklist IDs for consistent coloring
export function useChecklistIds(items: WatchlistItem[]) {
  return useMemo(() => getAllChecklistIds(items), [items]);
}

// Process items with filter and sort
export function useProcessedItems(
  items: WatchlistItem[],
  filter: string,
  sortColumn: SortColumn,
  sortDirection: SortDirection
) {
  return useMemo(
    () => processItems(items, filter, sortColumn, sortDirection),
    [items, filter, sortColumn, sortDirection]
  );
}

// Calculate stats for hero section
export function useWatchlistStats(items: WatchlistItem[]): WatchlistStats {
  return useMemo(() => calculateStats(items), [items]);
}

// Mutations
interface MutationCallbacks {
  onAddSuccess?: () => void;
  onAddError?: () => void;
  onRemoveSuccess?: () => void;
}

export function useWatchlistMutations(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  // Add to watchlist mutation
  const addMutation = useMutation({
    mutationFn: (productCode: string) => api.addToWatchlist(productCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      callbacks?.onAddSuccess?.();
    },
    onError: () => {
      callbacks?.onAddError?.();
    },
  });

  // Remove from watchlist mutation
  const removeMutation = useMutation({
    mutationFn: (productCode: string) => api.removeFromWatchlist(productCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      callbacks?.onRemoveSuccess?.();
    },
  });

  return {
    addMutation,
    removeMutation,
  };
}
